import React, { createContext, useState, useContext, useCallback, useEffect, useRef, useMemo } from 'react';
import { useFirebase } from './FirebaseContext';
import {
  getLibraryModules as apiGetLibraryModules,
  getPipelines as apiGetPipelines,
  getActivePipeline as apiGetActivePipeline,
  createPipeline as apiCreatePipeline,
  updatePipeline as apiUpdatePipeline,
  activatePipeline as apiActivatePipeline,
  deletePipeline as apiDeletePipeline,
  duplicatePipeline as apiDuplicatePipeline,
  getPipelineModules as apiGetPipelineModules,
  addModuleToPipeline as apiAddModuleToPipeline,
  updatePipelineModule as apiUpdatePipelineModule,
  removePipelineModule as apiRemovePipelineModule,
  runAnalysis as apiRunAnalysis,
  getHistory as apiGetHistory,
  updateWebhookEnabled as apiUpdateWebhookEnabled,
  regenerateWebhookSecret as apiRegenerateWebhookSecret,
} from '../api/analytics';
import { getBaseApiUrl } from '../api/core';
import { ENDPOINTS } from '../api/endpoints';
import logger from '../utils/logger';

const AnalyticsContext = createContext();

export const AnalyticsProvider = ({ children }) => {
  const { auth } = useFirebase();

  // Selected instrument (synced with main view)
  const [selectedInstrument, setSelectedInstrumentState] = useState(null);

  // Library modules (admin-managed)
  const [libraryModules, setLibraryModules] = useState([]);

  // User's pipelines
  const [pipelines, setPipelines] = useState([]);
  const [activePipeline, setActivePipeline] = useState(null);
  const [pipelineModules, setPipelineModules] = useState([]);

  // Analysis results with per-instrument LRU caching (max 20 instruments)
  const MAX_CACHE_SIZE = 20;
  const resultsCacheRef = useRef({});
  const [lastResult, setLastResult] = useState(null);
  const [streamingModules, setStreamingModules] = useState([]);
  const [streamingProgress, setStreamingProgress] = useState({ current: 0, total: 0 });

  // History
  const [history, setHistory] = useState([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState(null);

  // Module config modal state
  const [moduleConfigModalOpen, setModuleConfigModalOpen] = useState(false);
  const [selectedModuleForConfig, setSelectedModuleForConfig] = useState(null);

  // Streaming state
  const streamReaderRef = useRef(null);

  // Evict oldest cache entries when over the limit
  const evictCacheIfNeeded = useCallback(() => {
    const cache = resultsCacheRef.current;
    const keys = Object.keys(cache);
    if (keys.length <= MAX_CACHE_SIZE) return;

    // Sort by timestamp ascending (oldest first) and remove excess
    keys.sort((a, b) => (cache[a]?.timestamp || 0) - (cache[b]?.timestamp || 0));
    const toRemove = keys.length - MAX_CACHE_SIZE;
    for (let i = 0; i < toRemove; i++) {
      delete cache[keys[i]];
    }
  }, [MAX_CACHE_SIZE]);

  // ==================== Instrument Selection ====================

  // Track previous instrument and state refs to save cache on change
  const prevInstrumentRef = useRef(null);
  const lastResultRef = useRef(null);
  const streamingModulesRef = useRef([]);

  // Keep refs in sync with state
  useEffect(() => {
    lastResultRef.current = lastResult;
  }, [lastResult]);

  useEffect(() => {
    streamingModulesRef.current = streamingModules;
  }, [streamingModules]);

  const setSelectedInstrument = useCallback((instrument) => {
    const normalized = instrument ? instrument.replace('_', '').toUpperCase() : null;
    const prevInstrument = prevInstrumentRef.current;

    // Don't do anything if instrument hasn't changed
    if (prevInstrument === normalized) return;

    // Save current results for the previous instrument before switching
    if (prevInstrument && lastResultRef.current) {
      resultsCacheRef.current[prevInstrument] = {
        result: lastResultRef.current,
        streamingModules: streamingModulesRef.current,
        timestamp: Date.now(),
      };
      evictCacheIfNeeded();
    }

    // Update refs and state
    prevInstrumentRef.current = normalized;
    setSelectedInstrumentState(normalized);

    // Restore cached results for the new instrument, or clear
    const cached = normalized ? resultsCacheRef.current[normalized] : null;
    if (cached) {
      setLastResult(cached.result);
      setStreamingModules(cached.streamingModules || []);
    } else {
      setLastResult(null);
      setStreamingModules([]);
    }
    setStreamingProgress({ current: 0, total: 0 });
  }, []);

  // ==================== Library Modules ====================

  const loadLibraryModules = useCallback(async () => {
    if (!auth?.currentUser) return;

    apiGetLibraryModules(auth, (result) => {
      if (result && !result.error) {
        setLibraryModules(result);
      } else {
        logger.error('Failed to load library modules:', result?.error);
      }
    });
  }, [auth]);

  // ==================== Pipeline Management ====================

  const loadPipelines = useCallback(async () => {
    if (!auth?.currentUser || !selectedInstrument) {
      setPipelines([]);
      setActivePipeline(null);
      return;
    }

    setLoading(true);
    apiGetPipelines(auth, async (result) => {
      if (result && !result.error) {
        if (result.length > 0) {
          setPipelines(result);
          const active = result.find(p => p.isActive || p.active);
          if (active) {
            setActivePipeline(active);
          } else {
            setActivePipeline(result[0]);
          }
          setLoading(false);
        } else {
          // No pipelines exist - create a default one
          logger.info('No analytics pipelines found, creating default pipeline');
          apiCreatePipeline(auth, (createResult) => {
            if (createResult && !createResult.error) {
              setPipelines([createResult]);
              setActivePipeline(createResult);
            }
            setLoading(false);
          }, {
            instrument: selectedInstrument,
            name: 'Default',
            description: 'Default analytics pipeline'
          });
        }
      } else {
        setLoading(false);
      }
    }, selectedInstrument);
  }, [auth, selectedInstrument]);

  const createNewPipeline = useCallback(async (name, description, defaultTimeframe = 'H1') => {
    if (!auth?.currentUser || !selectedInstrument) return null;

    return new Promise((resolve) => {
      apiCreatePipeline(auth, (result) => {
        if (result && !result.error) {
          setPipelines(prev => [...prev, result]);
          resolve(result);
        } else {
          logger.error('Failed to create pipeline:', result?.error);
          resolve(null);
        }
      }, { instrument: selectedInstrument, name, description, defaultTimeframe });
    });
  }, [auth, selectedInstrument]);

  const switchPipeline = useCallback(async (pipelineId) => {
    if (!auth?.currentUser) return;

    const targetPipeline = pipelines.find(p => p.id === pipelineId);
    if (!targetPipeline) return;

    apiActivatePipeline(auth, (result) => {
      if (result && !result.error) {
        setPipelines(prev => prev.map(p => ({
          ...p,
          isActive: p.id === pipelineId,
          active: p.id === pipelineId,
        })));
        setActivePipeline({ ...targetPipeline, isActive: true, active: true });
        setLastResult(null); // Clear results when switching
      }
    }, pipelineId);
  }, [auth, pipelines]);

  const deletePipelineById = useCallback(async (pipelineId) => {
    if (!auth?.currentUser) return false;

    if (activePipeline?.id === pipelineId) {
      logger.warn('Cannot delete the active pipeline');
      return false;
    }

    return new Promise((resolve) => {
      apiDeletePipeline(auth, (result) => {
        if (result && !result.error) {
          setPipelines(prev => prev.filter(p => p.id !== pipelineId));
          resolve(true);
        } else {
          resolve(false);
        }
      }, pipelineId);
    });
  }, [auth, activePipeline]);

  const duplicatePipelineById = useCallback(async (pipelineId, newName) => {
    if (!auth?.currentUser) return null;

    return new Promise((resolve) => {
      apiDuplicatePipeline(auth, (result) => {
        if (result && !result.error) {
          setPipelines(prev => [...prev, result]);
          resolve(result);
        } else {
          resolve(null);
        }
      }, pipelineId, newName);
    });
  }, [auth]);

  // ==================== Pipeline Modules ====================

  const loadPipelineModules = useCallback(async (pipelineId) => {
    if (!auth?.currentUser || !pipelineId) return;

    apiGetPipelineModules(auth, (result) => {
      if (result && !result.error) {
        setPipelineModules(result);
      }
    }, pipelineId);
  }, [auth]);

  // Load modules when active pipeline changes
  useEffect(() => {
    if (activePipeline?.id) {
      loadPipelineModules(activePipeline.id);
    } else {
      setPipelineModules([]);
    }
  }, [activePipeline?.id, loadPipelineModules]);

  const addModule = useCallback(async (libraryModuleId, customConfigJson = null, weight = 1.0) => {
    if (!auth?.currentUser) {
      logger.error('addModule: No authenticated user');
      return null;
    }
    if (!activePipeline?.id) {
      logger.error('addModule: No active pipeline');
      return null;
    }

    return new Promise((resolve) => {
      apiAddModuleToPipeline(auth, (result) => {
        if (result && !result.error) {
          setPipelineModules(prev => [...prev, result]);
          resolve(result);
        } else {
          logger.error('addModule: API error', result?.error);
          resolve(null);
        }
      }, activePipeline.id, { libraryModuleId, customConfigJson, weight });
    });
  }, [auth, activePipeline?.id]);

  const updateModule = useCallback(async (moduleId, { customConfigJson, weight, enabled }) => {
    if (!auth?.currentUser || !activePipeline?.id) return null;

    return new Promise((resolve) => {
      apiUpdatePipelineModule(auth, (result) => {
        if (result && !result.error) {
          setPipelineModules(prev => prev.map(m => m.id === moduleId ? result : m));
          resolve(result);
        } else {
          resolve(null);
        }
      }, activePipeline.id, moduleId, { customConfigJson, weight, enabled });
    });
  }, [auth, activePipeline?.id]);

  const removeModule = useCallback(async (moduleId) => {
    if (!auth?.currentUser || !activePipeline?.id) return false;

    return new Promise((resolve) => {
      apiRemovePipelineModule(auth, (result) => {
        if (result && !result.error) {
          setPipelineModules(prev => prev.filter(m => m.id !== moduleId));
          resolve(true);
        } else {
          resolve(false);
        }
      }, activePipeline.id, moduleId);
    });
  }, [auth, activePipeline?.id]);

  // ==================== Module Config Modal ====================

  const openModuleConfigModal = useCallback((pipelineModule, libraryModule) => {
    setSelectedModuleForConfig({ pipelineModule, libraryModule });
    setModuleConfigModalOpen(true);
  }, []);

  const closeModuleConfigModal = useCallback(() => {
    setModuleConfigModalOpen(false);
    setSelectedModuleForConfig(null);
  }, []);

  // ==================== Analysis Execution ====================

  const runAnalysis = useCallback(async (timeframe = 'H1') => {
    if (!auth?.currentUser || !selectedInstrument) return;

    setAnalyzing(true);
    setError(null);
    setStreamingModules([]);

    apiRunAnalysis(auth, (result) => {
      if (result && !result.error) {
        const resultWithTimestamp = { ...result, timestamp: Date.now() };
        setLastResult(resultWithTimestamp);
        // Cache result for this instrument
        if (selectedInstrument) {
          resultsCacheRef.current[selectedInstrument] = {
            result: resultWithTimestamp,
            streamingModules: [],
            timestamp: Date.now(),
          };
          evictCacheIfNeeded();
        }
      } else {
        setError(result?.error || 'Analysis failed');
        setLastResult(null);
      }
      setAnalyzing(false);
    }, {
      instrument: selectedInstrument,
      timeframe,
      pipelineId: activePipeline?.id,
    });
  }, [auth, selectedInstrument, activePipeline?.id]);

  // Timeout per module during streaming (60s). If no SSE event arrives
  // within this window the stream is considered hung and we abort.
  const MODULE_TIMEOUT_MS = 60 * 1000;

  const runAnalysisStreaming = useCallback(async (timeframe = 'H1') => {
    if (!auth?.currentUser || !selectedInstrument) return;

    setAnalyzing(true);
    setError(null);
    setStreamingModules([]);
    setStreamingProgress({ current: 0, total: 0 });

    try {
      const idToken = await auth.currentUser.getIdToken(false);
      const url = getBaseApiUrl() + ENDPOINTS.ANALYTICS.ANALYZE_STREAM;

      const controller = new AbortController();
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          instrument: selectedInstrument,
          timeframe,
          pipelineId: activePipeline?.id,
        }),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      streamReaderRef.current = reader;
      const decoder = new TextDecoder();
      let buffer = '';
      const collectedModules = [];

      const processStream = async () => {
        let timeoutId = null;

        const resetTimeout = () => {
          if (timeoutId) clearTimeout(timeoutId);
          timeoutId = setTimeout(() => {
            logger.warn('Analytics stream timed out waiting for next event');
            reader.cancel();
            controller.abort();
            setError('Analysis timed out. Please try again.');
            setAnalyzing(false);
          }, MODULE_TIMEOUT_MS);
        };

        try {
          resetTimeout();

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            resetTimeout();
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (line.trim() === '') continue;

              const eventLines = line.split('\n');
              let eventName = 'message';
              let eventData = '';

              for (const eventLine of eventLines) {
                if (eventLine.startsWith('event:')) {
                  eventName = eventLine.substring(6).trim();
                } else if (eventLine.startsWith('data:')) {
                  eventData = eventLine.substring(5).trim();
                }
              }

              if (eventData) {
                try {
                  const data = JSON.parse(eventData);

                  switch (eventName) {
                    case 'start':
                      logger.log('Analytics started:', data);
                      setStreamingProgress({ current: 0, total: data.totalModules || 0 });
                      break;

                    case 'module':
                      logger.log('Module result:', data);
                      collectedModules.push(data);
                      setStreamingModules([...collectedModules]);
                      setStreamingProgress(prev => ({
                        ...prev,
                        current: data.currentIndex || collectedModules.length,
                      }));
                      break;

                    case 'complete':
                      logger.log('Analytics complete:', data);
                      const resultWithTimestamp = { ...data, timestamp: Date.now() };
                      setLastResult(resultWithTimestamp);
                      // Cache result for this instrument
                      if (selectedInstrument) {
                        resultsCacheRef.current[selectedInstrument] = {
                          result: resultWithTimestamp,
                          streamingModules: collectedModules,
                          timestamp: Date.now(),
                        };
                        evictCacheIfNeeded();
                      }
                      setAnalyzing(false);
                      break;

                    case 'error':
                      logger.error('Analytics error:', data);
                      setError(data.error || 'Analysis failed');
                      setAnalyzing(false);
                      break;

                    default:
                      logger.log('Unknown event:', eventName, data);
                  }
                } catch (parseError) {
                  logger.error('Error parsing SSE data:', parseError);
                }
              }
            }
          }
        } catch (streamError) {
          if (streamError.name === 'AbortError') return;
          logger.error('Stream error:', streamError);
          setError('Stream connection lost');
          setAnalyzing(false);
        } finally {
          if (timeoutId) clearTimeout(timeoutId);
        }
      };

      processStream();
    } catch (err) {
      if (err.name === 'AbortError') return;
      logger.error('Streaming analysis error:', err);
      setError(err.message || 'Analysis failed');
      setAnalyzing(false);
    }
  }, [auth, selectedInstrument, activePipeline?.id]);

  const cancelAnalysis = useCallback(() => {
    if (streamReaderRef.current) {
      streamReaderRef.current.cancel();
      streamReaderRef.current = null;
    }
    setAnalyzing(false);
  }, []);

  // ==================== Webhook ====================

  const updateWebhook = useCallback(async (enabled) => {
    if (!auth?.currentUser || !activePipeline?.id) return;

    apiUpdateWebhookEnabled(auth, (result) => {
      if (result && !result.error) {
        // Update webhookEnabled, webhookSecret, and webhookUrl from response
        setActivePipeline(prev => ({
          ...prev,
          webhookEnabled: result.webhookEnabled ?? enabled,
          webhookSecret: result.webhookSecret ?? prev.webhookSecret,
          webhookUrl: result.webhookUrl ?? prev.webhookUrl,
        }));
      }
    }, activePipeline.id, enabled);
  }, [auth, activePipeline?.id]);

  const regenerateWebhook = useCallback(async () => {
    if (!auth?.currentUser || !activePipeline?.id) return null;

    return new Promise((resolve) => {
      apiRegenerateWebhookSecret(auth, (result) => {
        if (result && !result.error) {
          setActivePipeline(prev => ({ ...prev, webhookSecret: result.secret }));
          resolve(result.secret);
        } else {
          resolve(null);
        }
      }, activePipeline.id);
    });
  }, [auth, activePipeline?.id]);

  // ==================== History ====================

  const loadHistory = useCallback(async (limit = 50, offset = 0) => {
    if (!auth?.currentUser) return;

    apiGetHistory(auth, (result) => {
      if (result && !result.error) {
        setHistory(result);
      }
    }, { instrument: selectedInstrument, limit, offset });
  }, [auth, selectedInstrument]);

  // ==================== Initialization ====================

  useEffect(() => {
    if (auth?.currentUser) {
      loadLibraryModules();
    }
  }, [auth?.currentUser, loadLibraryModules]);

  useEffect(() => {
    if (selectedInstrument && auth?.currentUser) {
      loadPipelines();
    }
  }, [selectedInstrument, auth?.currentUser, loadPipelines]);

  // ==================== Derived State ====================

  const getDirectionLabel = useMemo(() => {
    if (!lastResult) return null;
    switch (lastResult.direction) {
      case 'UP': return 'Bullish';
      case 'DOWN': return 'Bearish';
      case 'NEUTRAL': return 'Neutral';
      default: return lastResult.direction;
    }
  }, [lastResult]);

  // Check if results are stale (older than 5 minutes)
  const STALE_THRESHOLD_MS = 5 * 60 * 1000;
  const isResultStale = useMemo(() => {
    if (!lastResult?.timestamp) return false;
    return Date.now() - lastResult.timestamp > STALE_THRESHOLD_MS;
  }, [lastResult?.timestamp]);

  // Get time since last result
  const resultAge = useMemo(() => {
    if (!lastResult?.timestamp) return null;
    const ageMs = Date.now() - lastResult.timestamp;
    if (ageMs < 60000) return 'just now';
    if (ageMs < 3600000) return `${Math.floor(ageMs / 60000)}m ago`;
    return `${Math.floor(ageMs / 3600000)}h ago`;
  }, [lastResult?.timestamp]);

  const value = {
    // Instrument
    selectedInstrument,
    setSelectedInstrument,

    // Library modules
    libraryModules,
    loadLibraryModules,

    // Pipelines
    pipelines,
    activePipeline,
    loadPipelines,
    createNewPipeline,
    switchPipeline,
    deletePipelineById,
    duplicatePipelineById,

    // Pipeline modules
    pipelineModules,
    loadPipelineModules,
    addModule,
    updateModule,
    removeModule,

    // Analysis
    lastResult,
    streamingModules,
    streamingProgress,
    analyzing,
    runAnalysis,
    runAnalysisStreaming,
    cancelAnalysis,
    getDirectionLabel,
    isResultStale,
    resultAge,

    // Webhook
    updateWebhook,
    regenerateWebhook,

    // History
    history,
    loadHistory,

    // UI state
    loading,
    error,

    // Module config modal
    moduleConfigModalOpen,
    selectedModuleForConfig,
    openModuleConfigModal,
    closeModuleConfigModal,
  };

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};
